/**
 * LocationSwitcher — which location's settings are open, and a way to switch.
 * A button with the current location's name opens a search box: recent
 * locations first, then matches searched on the server a page at a time, so it
 * stays quick on accounts with hundreds of locations. Arrow keys move through
 * the list, Enter opens, Escape closes.
 *
 * When the server finds nothing in this account for the viewer (a system
 * administrator looking at another account), it falls back to the list the
 * host already has, filtered in the browser.
 */
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { sectionSpec, textRole, tokenVars } from '@turn-connect/ui';
import { TextInput } from '../../ui/ds';
import { ChevronDown, MapPin } from 'lucide-react';
import { useDebounce, useResponsive } from '@turn-connect/hooks';
import { adminService } from '../../../services/auth';
import { queryKeys } from '../../../queries/queryKeys';
import { signedInUserId } from '../../../utils/signedInUser';
import { readRecentLocations, rememberRecentLocation, type RecentLocation } from './recentLocations';
import '../../navigation/place/placeHeader.css';

export interface LocationSwitcherProps {
  accountId: string;
  current: RecentLocation;
  onSwitch: (locationId: string) => void;
  /** The host's own list, used when the server finds none for this viewer. */
  fallbackLocations?: RecentLocation[];
  /** 'title': the trigger is the page title ("Express Lane ▾", the shared page header); default a chip. */
  variant?: 'chip' | 'title';
}

const PAGE = 20;

export const LocationSwitcher: React.FC<LocationSwitcherProps> = ({
  accountId,
  current,
  onSwitch,
  fallbackLocations,
  variant = 'chip',
}) => {
  const userId = signedInUserId();
  const { isMobile: touch } = useResponsive();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const debounced = useDebounce(query.trim(), 250);
  const listId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [recents, setRecents] = useState<RecentLocation[]>(() => readRecentLocations(userId, accountId));

  // Opening a location's settings makes it recent.
  useEffect(() => {
    setRecents(rememberRecentLocation(userId, accountId, current));
  }, [userId, accountId, current.id, current.name]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set once the person turns out to be a member of none of this account's locations (below).
  const [useAccountList, setUseAccountList] = useState(false);
  const search = useQuery({
    queryKey: queryKeys.myLocationsSearch(userId, accountId, debounced),
    queryFn: () => adminService.searchMyLocationsInAccount(accountId, debounced, PAGE),
    enabled: open && !useAccountList,
    staleTime: 60_000,
    retry: false,
  });

  // Someone who works elsewhere (a system administrator opening another
  // account's location from the executive dashboard) is a member of none of
  // this account's locations: list the account's own locations instead.
  const noMemberships = !!search.data && search.data.total === 0 && !debounced;
  useEffect(() => {
    if (noMemberships && !fallbackLocations?.length) setUseAccountList(true);
  }, [noMemberships, fallbackLocations]);
  const accountList = useQuery({
    queryKey: [...queryKeys.myLocationsSearch(userId, accountId, debounced), 'account-list'],
    queryFn: () => adminService.getAccountLocationsPage(accountId, { search: debounced, limit: PAGE, offset: 0 }),
    enabled: open && useAccountList,
    staleTime: 60_000,
    retry: false,
  });

  // Whichever list is in use reports loading and failure.
  const activeQuery = useAccountList ? accountList : search;
  const results: RecentLocation[] = useMemo(() => {
    if (useAccountList) return (accountList.data?.items ?? []).map((l) => ({ id: l.id, name: l.name }));
    const server = search.data?.locations ?? [];
    if (server.length > 0 || (search.data && search.data.total > 0) || !fallbackLocations?.length) return server;
    const q = debounced.toLowerCase();
    return fallbackLocations.filter((l) => !q || l.name.toLowerCase().includes(q)).slice(0, PAGE);
  }, [useAccountList, accountList.data, search.data, fallbackLocations, debounced]);
  const total = useAccountList
    ? (accountList.data?.total ?? results.length)
    : search.data && search.data.total > 0
      ? search.data.total
      : results.length;
  const showRecents = !debounced && recents.filter((r) => r.id !== current.id).length > 0;
  const recentRows = showRecents ? recents.filter((r) => r.id !== current.id) : [];
  const rows = [...recentRows, ...results.filter((r) => !recentRows.some((x) => x.id === r.id))];

  useEffect(() => setActive(0), [debounced, open]);
  // Arrow keys move the active option while focus stays in the box: keep it in view.
  useEffect(() => {
    if (!open || !rows[active]) return;
    document.getElementById(`${listId}-${rows[active].id}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]); // eslint-disable-line react-hooks/exhaustive-deps
  // Close when focus or a click leaves the switcher.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [open]);

  const choose = (id: string) => {
    setOpen(false);
    setQuery('');
    if (id !== current.id) onSwitch(id);
  };

  const close = () => {
    setOpen(false);
    setQuery('');
    buttonRef.current?.focus();
  };

  return (
    <div ref={boxRef} style={{ position: 'relative', minWidth: 0 }}>
      {variant === 'title' ? (
        <button
          ref={buttonRef}
          type="button"
          className="ph-title-switch"
          data-testid="settings-location-switcher"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`${current.name}, switch location`}
          title="Switch location"
          onClick={() => setOpen((o) => !o)}
        >
          <span>{current.name}</span>
          <ChevronDown size={20} aria-hidden="true" className="ph-title-switch-chevron" />
        </button>
      ) : (
        <button
          ref={buttonRef}
          type="button"
          data-testid="settings-location-switcher"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`Location: ${current.name}. Switch location`}
          onClick={() => setOpen((o) => !o)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            maxWidth: '100%',
            ...textRole('button'),
            background: tokenVars.washStrong,
            border: `1px solid ${tokenVars.borderStrong}`,
            borderRadius: 6,
            padding: '0 10px',
            fontFamily: 'inherit',
            cursor: 'pointer',
            minHeight: touch ? 44 : 36,
          }}
        >
          <MapPin size={16} aria-hidden="true" style={{ color: tokenVars.textAccent, flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{current.name}</span>
          {/* Spelled out: a bare ▾ beside a name is easy to miss. */}
          <span
            aria-hidden
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              paddingLeft: 8,
              borderLeft: `1px solid ${tokenVars.border}`,
              ...textRole('meta', tokenVars.textAccent),
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            Switch location
            <ChevronDown size={14} />
          </span>
        </button>
      )}
      {open && (
        <div
          data-testid="settings-location-switcher-popover"
          style={{
            position: 'absolute',
            zIndex: 20,
            top: 'calc(100% + 6px)',
            left: 0,
            width: 'min(340px, calc(100vw - 32px))',
            background: tokenVars.surfaceRaised,
            border: `1px solid ${tokenVars.border}`,
            borderRadius: sectionSpec.radius,
            boxShadow: `0 16px 40px ${tokenVars.overlay}`,
            padding: 8,
          }}
        >
          <TextInput
            type="search"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={rows[active] ? `${listId}-${rows[active].id}` : undefined}
            aria-label="Find a location"
            placeholder="Find a location"
            data-testid="settings-location-search"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- opening the switcher is asking to search
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, rows.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              } else if (e.key === 'Enter' && rows[active]) {
                e.preventDefault();
                choose(rows[active].id);
              } else if (e.key === 'Escape') {
                e.preventDefault();
                close();
              }
            }}
          />
          <ul
            id={listId}
            role="listbox"
            aria-label="Locations"
            style={{ listStyle: 'none', margin: '6px 0 0', padding: 0, maxHeight: 320, overflowY: 'auto' }}
          >
            {rows.map((loc, i) => (
              <li
                key={loc.id}
                id={`${listId}-${loc.id}`}
                role="option"
                aria-selected={i === active}
                data-testid={`settings-location-option-${loc.id}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(loc.id)}
                style={{
                  ...textRole('body', 'primary'),
                  padding: '9px 10px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  minHeight: touch ? 44 : 36,
                  boxSizing: 'border-box',
                  background: i === active ? tokenVars.washStrong : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{loc.name}</span>
                {i < recentRows.length && <span style={{ ...textRole('meta'), flex: 'none' }}>Recent</span>}
              </li>
            ))}
          </ul>
          <div aria-live="polite" style={{ ...textRole('meta'), padding: '6px 4px 2px' }} data-testid="settings-location-status">
            {activeQuery.isFetching && rows.length === 0
              ? 'Searching…'
              : activeQuery.isError && rows.length === 0
                ? 'Couldn’t search locations. Try again in a moment.'
                : rows.length === 0
                  ? debounced
                    ? `No locations match “${debounced}”.`
                    : 'No other locations.'
                  : total > results.length
                    ? `Showing ${results.length} of ${total}. Type to narrow it down.`
                    : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSwitcher;
